import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-y-4 text-left",
  "data-testid": "send-record-step-2"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-bold text-textPrimary mb-1" }, "Plantão de origem", -1)
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = { class: "flex-1" }
const _hoisted_6 = { class: "block text-sm text-textPrimary" }
const _hoisted_7 = { class: "block text-xs text-neutral50" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm font-bold text-textPrimary mb-1" }, "Adicionar outros plantões", -1)
const _hoisted_9 = {
  key: 0,
  class: "flex flex-col items-start gap-1 text-sm text-negative",
  "data-testid": "send-record-stations-error"
}
const _hoisted_10 = {
  key: 1,
  class: "text-xs text-neutral50",
  "data-testid": "send-record-stations-loading"
}
const _hoisted_11 = {
  key: 2,
  class: "text-xs text-neutral50",
  "data-testid": "send-record-stations-empty"
}
const _hoisted_12 = {
  key: 3,
  class: "flex flex-col gap-1"
}
const _hoisted_13 = ["data-testid"]
const _hoisted_14 = ["checked", "data-testid", "onChange"]
const _hoisted_15 = { class: "flex-1" }
const _hoisted_16 = { class: "block text-sm text-textPrimary" }
const _hoisted_17 = { class: "block text-xs text-neutral50" }
const _hoisted_18 = { class: "flex flex-col items-end gap-1" }
const _hoisted_19 = ["data-testid"]
const _hoisted_20 = {
  key: 0,
  class: "rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-900",
  "data-testid": "send-record-already-ok-warning"
}
const _hoisted_21 = {
  class: "text-xs text-neutral50",
  "data-testid": "send-record-stations-summary"
}

import { computed } from 'vue'
import {
  EffectiveRegistrationStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS
} from '@/constants/ds-attendance.constants'
import { DEFAULT_UTC_OFFSET, formatIsoDateOnlyBR } from '@/utils/date'
import { isoToTimeInput, type SendRecordStationOption } from './types'

/**
 * Passo 2 de M2 — multi-plantão (Q15). O modelo continua "1 registro = 1
 * plantão": o multi-seleção é um atalho de UI que cria N registros irmãos com o
 * MESMO pacote de anexos.
 *
 * BF16 — `otherStations` some da lista já pronta e passa a vir da rota
 * `GET .../activity-records/candidates` (sempre do MESMO médico da origem);
 * as antigas abas "Deste médico"/"Desta escala" saem junto — sem outro médico
 * no contrato, a segunda aba nunca teria o que mostrar.
 */


export default /*#__PURE__*/_defineComponent({
  __name: 'StepStations',
  props: {
    origin: {},
    otherStations: {},
    selected: {},
    loading: { type: Boolean },
    error: {}
  },
  emits: ["update:selected", "retry"],
  setup(__props: any, { emit }) {

const props = __props;





const originStationId = computed(() => props.origin.doctorStationId)

const alreadyOkSelected = computed(() =>
  props.otherStations
    .concat(props.origin)
    .filter(
      (option) =>
        props.selected.includes(option.doctorStationId) &&
        option.registrationStatus === EffectiveRegistrationStatus.OK
    )
)

function toggle(doctorStationId: number, event: Event): void {
  const checked = (event.target as HTMLInputElement).checked
  const next = checked
    ? Array.from(new Set([...props.selected, doctorStationId]))
    : props.selected.filter((id) => id !== doctorStationId)
  emit('update:selected', next)
}

/** Horário e data SEMPRE na parede do plantão (`option.timezone`), nunca no fuso do navegador. */
function describe(option: SendRecordStationOption): string {
  const offset = option.timezone ?? DEFAULT_UTC_OFFSET
  const hours =
    option.expectedStart && option.expectedEnd
      ? `${isoToTimeInput(option.expectedStart, offset)}–${isoToTimeInput(option.expectedEnd, offset)}`
      : ''
  return [formatDate(option.referenceDate ?? option.expectedStart), hours, option.shiftName]
    .filter(Boolean)
    .join(' · ')
}

function subtitle(option: SendRecordStationOption): string {
  return [option.doctorName, option.healthUnitName, option.scheduleName].filter(Boolean).join(' · ')
}

function statusLabel(option: SendRecordStationOption): string {
  return EFFECTIVE_REGISTRATION_STATUS_LABELS[
    option.registrationStatus ?? EffectiveRegistrationStatus.NONE
  ]
}

function chipClass(option: SendRecordStationOption): string {
  const base = 'text-xs font-bold rounded-full px-2 py-0.5 h-fit whitespace-nowrap'
  switch (option.registrationStatus) {
    case EffectiveRegistrationStatus.OK:
      return `${base} bg-green-100 text-green-800`
    case EffectiveRegistrationStatus.PARTIAL:
      return `${base} bg-yellow-100 text-yellow-800`
    case EffectiveRegistrationStatus.PENDING:
      return `${base} bg-blue-100 text-blue-800`
    default:
      return `${base} bg-gray-100 text-gray-700`
  }
}

/**
 * `referenceDate`/`expectedStart` podem chegar como `yyyy-MM-dd` (contrato de
 * candidatos, BF16) ou ISO completo — nunca via `new Date()`: o construtor lê o
 * dia como meia-noite UTC e volta um dia num navegador em UTC-3.
 */
function formatDate(value?: string | null): string {
  return value ? formatIsoDateOnlyBR(value) : ''
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _hoisted_2,
      _createElementVNode("label", {
        class: "flex items-start gap-2 rounded-lg border border-primary bg-primary5/30 px-3 py-2",
        "data-testid": `send-record-station-row-${originStationId.value}`
      }, [
        _createElementVNode("input", {
          type: "checkbox",
          class: "mt-1",
          checked: "",
          disabled: "",
          "data-testid": `send-record-station-checkbox-${originStationId.value}`
        }, null, 8, _hoisted_4),
        _createElementVNode("span", _hoisted_5, [
          _createElementVNode("span", _hoisted_6, _toDisplayString(describe(_ctx.origin)), 1),
          _createElementVNode("span", _hoisted_7, _toDisplayString(subtitle(_ctx.origin)), 1)
        ]),
        _createElementVNode("span", {
          class: _normalizeClass(chipClass(_ctx.origin))
        }, _toDisplayString(statusLabel(_ctx.origin)), 3)
      ], 8, _hoisted_3)
    ]),
    _createElementVNode("div", null, [
      _hoisted_8,
      (_ctx.error)
        ? (_openBlock(), _createElementBlock("p", _hoisted_9, [
            _createElementVNode("span", null, _toDisplayString(_ctx.error), 1),
            _createElementVNode("button", {
              type: "button",
              class: "font-semibold underline",
              "data-testid": "send-record-stations-retry",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('retry')))
            }, " Tentar novamente ")
          ]))
        : (_ctx.loading)
          ? (_openBlock(), _createElementBlock("p", _hoisted_10, " Carregando outros plantões… "))
          : (!_ctx.otherStations.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, " Nenhum outro plantão do médico nos últimos 30 dias. "))
            : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.otherStations, (option) => {
                  return (_openBlock(), _createElementBlock("label", {
                    key: option.doctorStationId,
                    class: "flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2",
                    "data-testid": `send-record-station-row-${option.doctorStationId}`
                  }, [
                    _createElementVNode("input", {
                      type: "checkbox",
                      class: "mt-1",
                      checked: _ctx.selected.includes(option.doctorStationId),
                      "data-testid": `send-record-station-checkbox-${option.doctorStationId}`,
                      onChange: ($event: any) => (toggle(option.doctorStationId, $event))
                    }, null, 40, _hoisted_14),
                    _createElementVNode("span", _hoisted_15, [
                      _createElementVNode("span", _hoisted_16, _toDisplayString(describe(option)), 1),
                      _createElementVNode("span", _hoisted_17, _toDisplayString(subtitle(option)), 1)
                    ]),
                    _createElementVNode("span", _hoisted_18, [
                      _createElementVNode("span", {
                        class: _normalizeClass(chipClass(option))
                      }, _toDisplayString(statusLabel(option)), 3),
                      (option.hasOpenActivityRecord)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 0,
                            class: "text-xs font-bold rounded-full px-2 py-0.5 h-fit whitespace-nowrap bg-orange-100 text-orange-800",
                            "data-testid": `send-record-station-open-badge-${option.doctorStationId}`
                          }, " Registro em aberto ", 8, _hoisted_19))
                        : _createCommentVNode("", true)
                    ])
                  ], 8, _hoisted_13))
                }), 128))
              ]))
    ]),
    (alreadyOkSelected.value.length)
      ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(alreadyOkSelected.value.length === 1 ? 'Este plantão já tem' : 'Estes plantões já têm') + " as duas pernas por fonte de ranking maior; o registro será aceito mas pode não mudar o horário efetivo. ", 1))
      : _createCommentVNode("", true),
    _createElementVNode("p", _hoisted_21, _toDisplayString(_ctx.selected.length) + " " + _toDisplayString(_ctx.selected.length === 1 ? 'plantão selecionado gera' : 'plantões selecionados geram') + " " + _toDisplayString(_ctx.selected.length) + " " + _toDisplayString(_ctx.selected.length === 1 ? 'registro' : 'registros') + ", com os mesmos anexos compartilhados. ", 1)
  ]))
}
}

})